<template>
  <div
    role="dialog"
    class="modal modal-drawer-list-scroll fade fixed-end"
    aria-modal="true"
  >
    <div
      role="document"
      class="modal-dialog modal-dialog-vertical dw-11 dw-md-8 dw-lg-6 dw-xl-4"
    >
      <validation-observer tag="div" class="modal-content" @submit.native.prevent ref="validation">
        <div class="modal-header">
          <h2 class="modal-title">{{ isEdit ? 'Edit Rule' : 'Add Rule' }}</h2>
          <button
            type="button"
            data-bs-dismiss="modal"
            aria-label="Close"
            class="btn-close"
          ></button>
        </div>

        <div class="modal-body pt-0 pb-0 flex-grow-1">
          <div class="mt-3 mb-2">
            <validation-provider name="media_owner_id" vid="media_owner_id" rules="required">
              <label class="form-label">Media Owner</label>
              <media-owner-select v-model="mediaOwnerId" :multiple="false" :exclude-ids="excludedOwnerIds" />
              <c-help-block />
            </validation-provider>
          </div>

          <div class="mt-3 mb-2">
            <validation-provider name="fault_count_rule" vid="fault_count_rule" rules="required|numeric|min_value:1">
              <label class="form-label">Fault Count Rule</label>
              <c-input type="number" v-model="faultCountRule" />
              <c-help-block />
            </validation-provider>
          </div>

          <div class="mt-3 mb-2">
            <validation-provider name="time_period_rule" vid="time_period_rule" rules="required|numeric|min_value:1">
              <label class="form-label">Time Period Rule (days)</label>
              <c-input type="number" v-model="timePeriodRule" />
              <c-help-block />
            </validation-provider>
          </div>

          <div class="mt-3 mb-2">
            <validation-provider name="repeating_rule" vid="repeating_rule" rules="required">
              <label class="form-label">Repeating Rule</label>
              <c-input
                as="multiselect"
                v-model="repeatingRuleSelected"
                placeholder="Select"
                :show-labels="false"
                :options="repeatingRuleOptions"
                label="name"
                track-by="value"
              />
              <c-help-block />
            </validation-provider>
          </div>
        </div>

        <div class="modal-footer">
          <c-button
            v-if="isEdit"
            type="button"
            class="btn btn-danger me-auto"
            :disabled="saving"
            @click="handleDelete"
          >
            Delete
          </c-button>
          <button
            type="button"
            data-bs-dismiss="modal"
            class="btn btn-outline-info"
          >
            Close
          </button>
          <c-button
            type="button"
            class="btn btn-primary"
            :disabled="saving"
            @click="handleSave"
          >
            <span v-if="saving"><i class="far fa-spinner-third fa-spin me-1"></i>Saving...</span>
            <span v-else>{{ isEdit ? 'Save' : 'Add Rule' }}</span>
          </c-button>
        </div>
      </validation-observer>
    </div>
  </div>
</template>

<script>
import { helperMethods } from '@/vuex/modules/settings/report-rules'
import MediaOwnerSelect from '@/components/media-owners/MediaOwnerSelect'
import { CModal, toast } from '@chameleon/chameleon-vue'
import swal from 'sweetalert'

export default {
  extends: CModal,

  components: {
    MediaOwnerSelect
  },

  props: {
    rule: {
      type: Object,
      default: null
    },
    excludedIds: {
      type: Array,
      default: () => []
    }
  },

  data () {
    return {
      mediaOwnerId: null,
      faultCountRule: null,
      timePeriodRule: 30,
      repeatingRule: 'Rolling',
      saving: false,
      repeatingRuleOptions: [
        { name: 'Rolling', value: 'Rolling' }
      ]
    }
  },

  watch: {
    rule: {
      handler (rule) {
        if (rule) {
          this.mediaOwnerId = rule.media_owner_id
          this.faultCountRule = rule.fault_count_rule
          this.repeatingRule = rule.repeating_rule
          this.timePeriodRule = rule.time_period_rule
        } else {
          this.resetForm()
        }
      },
      immediate: true
    }
  },

  computed: {
    isEdit () {
      return !!this.rule
    },

    excludedOwnerIds () {
      if (!this.excludedIds || !this.excludedIds.length) return []
      if (this.isEdit && this.rule) {
        return this.excludedIds.filter(id => id !== this.rule.media_owner_id)
      }
      return this.excludedIds
    },

    repeatingRuleSelected: {
      get () {
        return this.repeatingRuleOptions.find(option => option.value === this.repeatingRule)
      },
      set (value) {
        this.repeatingRule = value ? value.value : 'Rolling'
      }
    }
  },

  async mounted () {
    this.addEventListener('open', () => {
      this.resetForm()
      if (this.rule) {
        this.mediaOwnerId = this.rule.media_owner_id
        this.faultCountRule = this.rule.fault_count_rule
        this.repeatingRule = this.rule.repeating_rule
        this.timePeriodRule = this.rule.time_period_rule
      }
    })

    this.addEventListener('close', () => {
      this.resetForm()
    })
  },

  methods: {
    ...helperMethods,

    resetForm () {
      this.mediaOwnerId = null
      this.faultCountRule = null
      this.timePeriodRule = 30
      this.repeatingRule = 'Rolling'
      this.saving = false
      if (this.$refs.validation) {
        this.$refs.validation.reset()
      }
    },

    async handleSave () {
      const ok = await this.$refs.validation.validate()
      if (!ok) return

      this.saving = true
      try {
        const payload = {
          media_owner_id: this.mediaOwnerId,
          fault_count_rule: Number(this.faultCountRule),
          time_period_rule: Number(this.timePeriodRule),
          repeating_rule: this.repeatingRule
        }
        if (this.isEdit) {
          await this.updateReportRule({ id: this.rule.id, payload })
          toast('Rule updated successfully', 'success')
        } else {
          await this.storeReportRule(payload)
          toast('Rule created successfully', 'success')
        }
        this.$emit('saved')
        this.close()
      } catch (error) {
        toast('Failed to save rule', 'danger')
      } finally {
        this.saving = false
      }
    },

    async handleDelete () {
      if (
        !await swal({
          text: 'Are you sure you want to delete this rule?',
          buttons: {
            ok: { text: 'Yes', visible: true, className: 'btn btn-danger' },
            cancel: { text: 'No', visible: true, className: 'btn btn-dark' }
          },
          dangerMode: true
        })
      ) {
        return
      }

      this.saving = true
      try {
        await this.deleteReportRule(this.rule.id)
        toast('Rule deleted successfully', 'success')
        this.$emit('saved')
        this.close()
      } catch (error) {
        toast('Failed to delete rule', 'danger')
      } finally {
        this.saving = false
      }
    }
  }
}
</script>
